import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useLoginUserMutation } from "@/features/auth/api/authAPI";
import { userActions } from "@/entities/user";
import { toast } from 'react-toastify';

const useLogin = () => {
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [needsVerification, setNeedsVerification] = React.useState(false);
  const dispatch = useDispatch();
 
  const navigate = useNavigate();
  const [loginUser] = useLoginUserMutation();
  const location = useLocation();
  const from = location.state?.from || "/";

  const handleLogin = async (
    username: string,
    password: string,
    remember: boolean,
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setNeedsVerification(false);

    try {
      if (!username.trim()) {
        throw new Error("Username is required");
      }
      if (!password) {
        throw new Error("Password is required");
      }

      const formData = new FormData();
      formData.append("username", username.trim()); 
      formData.append("password", password);
      formData.append("grant_type", "password");
      formData.append("client_id", import.meta.env.VITE_API_CLIENT_ID)

      await loginUser(formData).unwrap();

      if (remember) {
        localStorage.setItem('username', username)
        localStorage.setItem('password', password)

      } else {
        localStorage.removeItem('username')
        localStorage.removeItem('password')
      }

      dispatch(userActions.login());
      dispatch(userActions.setWithoutLoading(false));
      navigate(from, { replace: true, state: { fromLogin: true } });
      window.location.reload();

      
    } catch (error) {
      if (error.data?.detail === "you need to verify your email address to be able to login") {
        setNeedsVerification(true);
        toast.error("Please verify your email address before logging in");
        return;
      }

      if (error instanceof Error) {
        if (error.message === "Username is required") {
          toast.error("Username is required. Please enter a username.");
        } else if (error.message === "Password is required") {
          toast.error("Password is required. Please enter a password.");
        } else {
          // Handle other Error instances
          toast.error(error.message);
        }
      } else if (axios.isAxiosError(error)) {
        console.error("Login failed:", error.response?.data.error);
        toast.error(error.response?.data.error);
      } else {

        const errMsg = error.data?.error_description || error.data?.detail
        toast.error(errMsg)
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  return { isLoggingIn, handleLogin, needsVerification };
};

export default useLogin;
